import { ScrollableCurriculumTabStrip } from "@/components/ui/scrollable-curriculum-tab-strip";
import { Tabs, TabsContent, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { BeltRank } from "../../MembersArea";

const DEFAULT_TAB_SURFACE = "!bg-gray-200 !text-gray-900";

interface PalBahngMooGongProps {
  selectedBelt: BeltRank;
  getBeltColor: (belt: BeltRank) => string;
  presentation?: "legacy" | "curriculum";
  beltTabSurfaceClass?: string;
}

function PalBahngInner() {
  return (
    <div className="space-y-4">
      <div>
        <h4 className="mb-3 text-base font-medium text-taekwondo-red">Pattern Information</h4>
        <p className="mb-4 text-gray-700">PalBahngMooGong - meaning is Eight Guardians of the Peace.</p>
      </div>
      <div>
        <h4 className="mb-3 text-base font-medium text-taekwondo-red">Meaning</h4>
        <p className="text-gray-700">
          PalBahngMooGong translates to &quot;Eight Directional Martial Achievement&quot; and represents
          complete mastery of techniques in all directions. The pattern emphasizes the importance of being
          prepared and capable in any situation, from any direction.
        </p>
      </div>
      <div>
        <h4 className="mb-3 text-base font-medium text-taekwondo-red">Movements</h4>
        <p className="text-gray-700">
          The pattern consists of 66 movements that demonstrate advanced techniques in eight different
          directions. Each sequence emphasizes directional awareness, balance, and complete mastery of
          advanced techniques.
        </p>
      </div>
      <div>
        <h4 className="mb-3 text-base font-medium text-taekwondo-red">Key Techniques</h4>
        <ul className="list-inside list-disc space-y-1 text-gray-700">
          <li>Multi-directional techniques</li>
          <li>Advanced stance transitions</li>
          <li>Complex combination movements</li>
          <li>Directional awareness drills</li>
          <li>Power generation in any direction</li>
          <li>Advanced balance control</li>
        </ul>
      </div>
      <div>
        <h4 className="mb-3 text-base font-medium text-taekwondo-red">Required Patterns for Grading</h4>
        <ul className="list-inside list-disc space-y-1 text-gray-700">
          <li>1st Dan: Patterns 1-5 - Can be substitued for a creative form of 30 or more movements</li>
          <li>2nd Dan: Patterns 1-6</li>
          <li>3rd Dan: Patterns 1-7</li>
          <li>4th Dan: Patterns 1-8 (Complete Pattern)</li>
        </ul>
      </div>
    </div>
  );
}

export default function PalBahngMooGong({
  selectedBelt,
  getBeltColor: _getBeltColor,
  presentation = "legacy",
  beltTabSurfaceClass = DEFAULT_TAB_SURFACE,
}: PalBahngMooGongProps) {
  if (!["BLACK/D1", "BLACK/D2", "BLACK/D3", "BLACK/D4", "BLACK/D5"].includes(selectedBelt)) {
    return null;
  }

  const surface = beltTabSurfaceClass;

  if (presentation === "curriculum") {
    return (
      <div className="rounded-lg bg-gray-50 p-4 md:p-5">
        <Tabs defaultValue="pattern-information" className="w-full space-y-4">
          <ScrollableCurriculumTabStrip ariaLabelPrefix="PalBahng Moo Gong">
            <TabsTrigger
              value="pattern-information"
              className={cn(
                "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/10 px-3 py-2 text-xs font-medium md:text-sm",
                surface,
                "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
              )}
            >
              Pattern Information
            </TabsTrigger>
            <TabsTrigger
              value="meaning"
              className={cn(
                "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/10 px-3 py-2 text-xs font-medium md:text-sm",
                surface,
                "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
              )}
            >
              Meaning
            </TabsTrigger>
            <TabsTrigger
              value="movements"
              className={cn(
                "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/10 px-3 py-2 text-xs font-medium md:text-sm",
                surface,
                "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
              )}
            >
              Movements
            </TabsTrigger>
            <TabsTrigger
              value="key-techniques"
              className={cn(
                "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/10 px-3 py-2 text-xs font-medium md:text-sm",
                surface,
                "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
              )}
            >
              Key Techniques
            </TabsTrigger>
            <TabsTrigger
              value="grading"
              className={cn(
                "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/10 px-3 py-2 text-xs font-medium md:text-sm",
                surface,
                "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
              )}
            >
              Grading Requirements
            </TabsTrigger>
          </ScrollableCurriculumTabStrip>

          <TabsContent value="pattern-information" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-3 text-base font-medium text-taekwondo-red">Pattern Information</h4>
              <p className="text-gray-700">
                PalBahngMooGong - meaning is Eight Guardians of the Peace.
              </p>
            </div>
          </TabsContent>
          <TabsContent value="meaning" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-3 text-base font-medium text-taekwondo-red">Meaning</h4>
              <p className="text-gray-700">
                PalBahngMooGong translates to &quot;Eight Directional Martial Achievement&quot; and represents
                complete mastery of techniques in all directions. The pattern emphasizes the importance of
                being prepared and capable in any situation, from any direction.
              </p>
            </div>
          </TabsContent>
          <TabsContent value="movements" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-3 text-base font-medium text-taekwondo-red">Movements</h4>
              <p className="text-gray-700">
                The pattern consists of 66 movements that demonstrate advanced techniques in eight different
                directions. Each sequence emphasizes directional awareness, balance, and complete mastery of
                advanced techniques.
              </p>
            </div>
          </TabsContent>
          <TabsContent value="key-techniques" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-3 text-base font-medium text-taekwondo-red">Key Techniques</h4>
              <ul className="list-inside list-disc space-y-1 text-gray-700">
                <li>Multi-directional techniques</li>
                <li>Advanced stance transitions</li>
                <li>Complex combination movements</li>
                <li>Directional awareness drills</li>
                <li>Power generation in any direction</li>
                <li>Advanced balance control</li>
              </ul>
            </div>
          </TabsContent>
          <TabsContent value="grading" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-3 text-base font-medium text-taekwondo-red">Required Patterns for Grading</h4>
              <ul className="list-inside list-disc space-y-1 text-gray-700">
                <li>1st Dan: Patterns 1-5 - Can be substitued for a creative form of 30 or more movements</li>
                <li>2nd Dan: Patterns 1-6</li>
                <li>3rd Dan: Patterns 1-7</li>
                <li>4th Dan: Patterns 1-8 (Complete Pattern)</li>
              </ul>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  return (
    <div className="mt-2 rounded-lg bg-gray-50 p-4">
      <PalBahngInner />
    </div>
  );
}
